/**
 * Build React Flow nodes + edges from a TreeNode, with dagre layout.
 */
import dagre from "dagre";
import type { Edge, Node as RFNode } from "@xyflow/react";

import type { TreeNode } from "./types";

export interface RFData {
  nodes: RFNode[];
  edges: Edge[];
}

const NODE_H = 44;
const PX_PER_CHAR = 7.2;
const NODE_MIN_W = 150;
const NODE_PAD = 28;

function estimateWidth(t: TreeNode): number {
  const titleLen = (t.title || "untitled").length;
  return Math.max(NODE_MIN_W, Math.min(320, titleLen * PX_PER_CHAR + NODE_PAD));
}

export function buildGraph(tree: TreeNode | null): RFData {
  if (!tree) return { nodes: [], edges: [] };

  const rfNodes: RFNode[] = [];
  const edges: Edge[] = [];
  const widths = new Map<string, number>();

  const walk = (t: TreeNode) => {
    const w = estimateWidth(t);
    widths.set(t.id, w);
    rfNodes.push({
      id: t.id,
      type: "mindmap",
      data: { node: t } as Record<string, unknown>,
      position: { x: 0, y: 0 },
      width: w,
      height: NODE_H,
    });
    for (const c of t.children) {
      edges.push({
        id: `${t.id}->${c.id}`,
        source: t.id,
        target: c.id,
        type: "smoothstep",
      });
      walk(c);
    }
  };
  walk(tree);

  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: "LR", nodesep: 12, ranksep: 36, ranker: "tight-tree" });
  g.setDefaultEdgeLabel(() => ({}));
  for (const n of rfNodes) g.setNode(n.id, { width: widths.get(n.id) ?? NODE_MIN_W, height: NODE_H });
  for (const e of edges) g.setEdge(e.source, e.target);
  dagre.layout(g);

  for (const n of rfNodes) {
    const pos = g.node(n.id);
    const w = widths.get(n.id) ?? NODE_MIN_W;
    n.position = { x: pos.x - w / 2, y: pos.y - NODE_H / 2 };
  }

  return { nodes: rfNodes, edges };
}

export function flattenTree(tree: TreeNode | null): TreeNode[] {
  if (!tree) return [];
  const out: TreeNode[] = [];
  const walk = (t: TreeNode) => {
    out.push(t);
    t.children.forEach(walk);
  };
  walk(tree);
  return out;
}
